import { FormEvent, useEffect, useState } from 'react';

import { Link, router, usePage } from '@inertiajs/react';
import { Heart, Pencil, Plus, Trash2 } from 'lucide-react';

import { WishlistProductSearchCard } from './WishlistProductSearchCard';
import { InfiniteScrollTrigger } from '../../../components/shared/InfiniteScrollTrigger';
import { Button } from '../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/Input';
import { Routes, wishlistCollectionRoute } from '../../../models/routes';
import { csrfTokenHeader } from '../../../services/CsrfTokenService';
import { CartonProduct } from '../../../types';
import { cn } from '../../../utils';
import { ProductCardsGrid } from '../../Products/ProductCardsGrid';

type WishlistCollection = {
    id: number;
    name: string;
    itemsCount: number;
};

type PageProps = {
    products: CartonProduct[];
    collections: WishlistCollection[];
    selectedCollectionId: number | null;
    nextPage: number | null;
};

const requestJson = async <T,>(
    url: string,
    options: RequestInit,
): Promise<T> => {
    const response = await fetch(url, {
        ...options,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...csrfTokenHeader(),
            ...options.headers,
        },
    });
    const payload = response.status === 204 ? null : await response.json();

    if (!response.ok) {
        const message = payload?.errors
            ? Object.values(payload.errors).join(' ')
            : 'Не вдалося зберегти колекцію';
        throw new Error(message);
    }

    return payload as T;
};

const collectionUrl = (collectionId?: number | null) =>
    collectionId
        ? `${Routes.Wishlist}?collection=${collectionId}`
        : Routes.Wishlist;

const Wishlist = () => {
    const { products, collections, selectedCollectionId, nextPage } =
        usePage<PageProps>().props;
    const selectedCollection = collections.find(
        (collection) => collection.id === selectedCollectionId,
    );
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editingCollection, setEditingCollection] =
        useState<WishlistCollection | null>(null);
    const [name, setName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        setName(editingCollection?.name ?? '');
        setError(null);
    }, [editingCollection, formOpen]);

    const openCreateDialog = () => {
        setEditingCollection(null);
        setFormOpen(true);
    };

    const openEditDialog = () => {
        if (!selectedCollection) return;
        setEditingCollection(selectedCollection);
        setFormOpen(true);
    };

    const saveCollection = async (event: FormEvent) => {
        event.preventDefault();
        if (!name.trim()) return;

        setProcessing(true);
        setError(null);
        try {
            await requestJson<WishlistCollection>(
                editingCollection
                    ? wishlistCollectionRoute(editingCollection.id)
                    : Routes.WishlistCollections,
                {
                    method: editingCollection ? 'PATCH' : 'POST',
                    body: JSON.stringify({
                        wishlist_collection: { name },
                    }),
                },
            );
            setFormOpen(false);
            router.reload({ only: ['collections'] });
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setProcessing(false);
        }
    };

    const deleteCollection = async () => {
        if (!selectedCollection) return;

        setProcessing(true);
        setError(null);
        try {
            await requestJson(wishlistCollectionRoute(selectedCollection.id), {
                method: 'DELETE',
            });
            setDeleteOpen(false);
            router.get(Routes.Wishlist);
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setProcessing(false);
        }
    };

    const loadMore = () => {
        if (!nextPage || loadingMore) return;

        router.get(
            Routes.Wishlist,
            {
                ...(selectedCollectionId
                    ? { collection: selectedCollectionId }
                    : {}),
                page: nextPage,
            },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['products', 'nextPage'],
                onStart: () => setLoadingMore(true),
                onFinish: () => setLoadingMore(false),
            },
        );
    };

    return (
        <div className="space-y-6 py-6">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <h1 className="text-2xl font-bold sm:text-3xl">
                        Список бажаного
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Зберігайте бажані товари та групуйте їх у колекції.
                    </p>
                </div>
                <Button className="gap-2" onClick={openCreateDialog}>
                    <Plus className="h-4 w-4" />
                    Створити колекцію
                </Button>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1">
                <Button
                    asChild
                    variant={selectedCollectionId ? 'outline' : 'default'}
                    size="sm"
                    className="shrink-0"
                >
                    <Link href={Routes.Wishlist}>Загальний</Link>
                </Button>
                {collections.map((collection) => (
                    <Button
                        key={collection.id}
                        asChild
                        variant={
                            collection.id === selectedCollectionId
                                ? 'default'
                                : 'outline'
                        }
                        size="sm"
                        className="shrink-0"
                    >
                        <Link href={collectionUrl(collection.id)}>
                            {collection.name}
                            <span
                                className={cn(
                                    'ml-2 rounded-full px-1.5 text-xs',
                                    collection.id === selectedCollectionId
                                        ? 'bg-primary-foreground/20'
                                        : 'bg-muted',
                                )}
                            >
                                {collection.itemsCount}
                            </span>
                        </Link>
                    </Button>
                ))}
            </div>

            {selectedCollection && (
                <div className="flex items-center justify-between rounded-xl border bg-muted/20 px-4 py-3">
                    <div>
                        <h2 className="font-semibold">
                            {selectedCollection.name}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {selectedCollection.itemsCount} товарів
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={openEditDialog}
                            aria-label="Перейменувати колекцію"
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setDeleteOpen(true)}
                            aria-label="Видалити колекцію"
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                </div>
            )}

            {products.length === 0 && (
                <div className="rounded-2xl border bg-muted/10 px-4 py-12 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <Heart className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h2 className="text-lg font-medium">
                        {selectedCollection
                            ? 'У цій колекції ще немає товарів'
                            : 'Ваш список бажаного поки порожній'}
                    </h2>
                    <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
                        Скористайтеся карткою «Додати товар» нижче або натисніть
                        серце в каталозі.
                    </p>
                    <Button asChild className="mt-5">
                        <Link href={Routes.Home}>Перейти до каталогу</Link>
                    </Button>
                </div>
            )}

            <ProductCardsGrid
                products={products}
                testId="wishlist-products-grid"
                manageWishlistCollections
                childrenPosition="start"
            >
                <WishlistProductSearchCard
                    collectionId={selectedCollectionId}
                    collectionName={selectedCollection?.name}
                    existingProductIds={products.map((product) => product.id)}
                    onAdded={() =>
                        router.reload({
                            only: [
                                'products',
                                'collections',
                                'nextPage',
                                'accountNav',
                            ],
                        })
                    }
                />
            </ProductCardsGrid>

            {nextPage && (
                <InfiniteScrollTrigger
                    loading={loadingMore}
                    onLoadMore={loadMore}
                />
            )}

            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="max-w-md">
                    <form onSubmit={saveCollection} className="space-y-4">
                        <DialogHeader>
                            <DialogTitle>
                                {editingCollection
                                    ? 'Перейменувати колекцію'
                                    : 'Нова колекція'}
                            </DialogTitle>
                            <DialogDescription>
                                Назва буде видима лише вам.
                            </DialogDescription>
                        </DialogHeader>
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            maxLength={60}
                            placeholder="Наприклад, Купити цього місяця"
                            aria-label="Назва колекції"
                            autoFocus
                        />
                        {error && (
                            <p
                                className="text-sm text-destructive"
                                role="alert"
                            >
                                {error}
                            </p>
                        )}
                        <DialogFooter>
                            <Button
                                type="submit"
                                disabled={processing || !name.trim()}
                            >
                                Зберегти
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Видалити колекцію?</DialogTitle>
                        <DialogDescription>
                            Товари залишаться в загальному списку.
                        </DialogDescription>
                    </DialogHeader>
                    {error && (
                        <p className="text-sm text-destructive" role="alert">
                            {error}
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setDeleteOpen(false)}
                        >
                            Скасувати
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={deleteCollection}
                            disabled={processing}
                        >
                            Видалити
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Wishlist;
